/**
 * The sideloaded Android build, as the web client sees it.
 *
 * Everything here is about ONE object that the release workflow publishes beside the .apk:
 * `android/latest.json`, a small manifest naming the newest build. That is the only way this
 * page can print a version, a size and a date — the web client and the mobile release are
 * deployed separately, so anything baked into this bundle at build time would be stale the
 * moment a phone build shipped without a web deploy.
 *
 * **The manifest names an IMMUTABLE key**, which is the whole cache story. The pointer
 * (`android/chompdome-latest.apk`) is overwritten every release and therefore lives or dies on
 * Cloudflare cache rules; a versioned key never changes, so once this page has read the
 * manifest it links something that can be cached forever and can never be the wrong build.
 * The manifest itself is the only short-lived object in the chain.
 *
 * The short URL (`/android/latest`) is deliberately NOT what the button links to: it is a
 * Cloudflare Worker redirect for a human to type or share, and this page already holds the
 * exact key it would have redirected to. See `deploy/cloudflare/downloads-worker`.
 */

import { z } from 'zod';

/** The R2 bucket's custom domain. Public, and the same host `docs/mobile.md` documents. */
export const DOWNLOADS_ORIGIN = 'https://downloads.chompdome.com';

/** Published by `.github/workflows/mobile-android-release.yml` on every release. */
export const ANDROID_MANIFEST_URL = `${DOWNLOADS_ORIGIN}/android/latest.json`;

/** The short, stable URL a human types. Served by the Worker, not by R2. */
export const ANDROID_SHORT_URL = `${DOWNLOADS_ORIGIN}/android/latest`;

/**
 * The overwritten pointer key, which works with no Worker and no manifest.
 *
 * This is the fallback the button falls back TO: if the manifest cannot be read the page still
 * has to hand somebody a working download, and this key has been the stable link all along.
 */
export const ANDROID_POINTER_URL = `${DOWNLOADS_ORIGIN}/android/chompdome-latest.apk`;

/**
 * A key is turned into a URL and put in an `href`, so its shape is checked rather than trusted:
 * inside the `android/` prefix, no path traversal, and an `.apk` suffix — a file Android's
 * installer will not open under any other name.
 */
const APK_KEY = /^android\/[A-Za-z0-9._-]+\.apk$/;

export const androidReleaseSchema = z.object({
  version: z.string().regex(/^\d+\.\d+\.\d+$/),
  buildNumber: z.number().int().positive(),
  key: z.string().regex(APK_KEY),
  sizeBytes: z.number().int().positive(),
  releasedAt: z.string().datetime(),
});

export type AndroidRelease = z.infer<typeof androidReleaseSchema>;

/** The immutable, versioned download for a release. */
export function apkUrl(release: AndroidRelease): string {
  return `${DOWNLOADS_ORIGIN}/${release.key}`;
}

/** What the visitor is holding, as far as this page needs to care. */
export type DownloadPlatform = 'android' | 'ios' | 'other';

/**
 * Which of the three answers to give.
 *
 * A user agent is a hint rather than a fact, and it is used here only to decide which call to
 * action leads — never to withhold anything — so a wrong guess costs a reader one extra
 * sentence rather than a download they cannot reach.
 *
 * `maxTouchPoints` is what separates an iPad from a Mac: since iPadOS 13 the tablet reports
 * itself as `Macintosh`, and the touch count is the only thing left that gives it away.
 */
export function detectDownloadPlatform(userAgent: string, maxTouchPoints = 0): DownloadPlatform {
  const agent = userAgent.toLowerCase();
  if (agent.includes('android')) return 'android';
  if (/iphone|ipad|ipod/.test(agent)) return 'ios';
  if (agent.includes('macintosh') && maxTouchPoints > 1) return 'ios';
  return 'other';
}

/** What this browser is, read off `navigator`. `'other'` anywhere there is no `navigator`. */
export function currentDownloadPlatform(): DownloadPlatform {
  if (typeof navigator === 'undefined') return 'other';
  return detectDownloadPlatform(navigator.userAgent, navigator.maxTouchPoints ?? 0);
}

/**
 * An APK's size, in the units a phone shows in its download shade.
 *
 * Binary megabytes, because that is what Android's own downloader and file manager report, and
 * a page that said 25.6 MB against a notification saying 24.4 MB would read as the wrong file.
 */
export function formatApkSize(bytes: number): string {
  const megabytes = bytes / (1024 * 1024);
  return `${megabytes.toFixed(1)} MB`;
}

/**
 * When the build was published, in UTC.
 *
 * UTC and a fixed format rather than the reader's locale, for the leaderboard's reason: the
 * instant is a property of the release, not of who is looking at it, and a build published at
 * 23:30Z should not be dated a day apart for two people comparing the same page.
 */
const RELEASE_DATE_FORMAT = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

export function formatReleaseDate(iso: string): string {
  return RELEASE_DATE_FORMAT.format(new Date(iso));
}

/**
 * Read the manifest.
 *
 * Rejects on anything at all — a 404 because no release has been published, a network error, a
 * missing CORS header because the Worker is not deployed yet, or a body that does not parse.
 * The page treats every one of those the same way, because the answer to all of them is the
 * same: draw the button against the stable pointer and say nothing about a version.
 */
export async function fetchAndroidRelease(signal?: AbortSignal): Promise<AndroidRelease> {
  const response = await fetch(ANDROID_MANIFEST_URL, { signal });
  if (!response.ok) throw new Error(`manifest ${response.status}`);
  return androidReleaseSchema.parse(await response.json());
}
